import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/core/Dialog';
import { IconExclamationTriangle } from '@/icons';

interface InactivityWarningDialogProps {
  open: boolean;
  remainingSeconds: number;
}

export const InactivityWarningDialog = ({
  open,
  remainingSeconds,
}: InactivityWarningDialogProps) => {
  const [timeLeft, setTimeLeft] = useState(remainingSeconds);

  useEffect(() => {
    setTimeLeft(remainingSeconds);
  }, [remainingSeconds]);

  useEffect(() => {
    if (!open) return;

    const interval = setInterval(() => {
      setTimeLeft((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(interval);
  }, [open]);

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="w-full min-w-0 max-w-xl gap-6 p-8 text-text-950"
        aria-describedby="inactivity-description"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div
              className="flex size-10 items-center justify-center rounded-full bg-yellow-100"
              aria-hidden="true"
            >
              <IconExclamationTriangle className="size-6 text-yellow-600" />
            </div>
            <DialogTitle className="mb-0">
              <FormattedMessage defaultMessage="Din session håller på att löpa ut" />
            </DialogTitle>
          </div>
        </DialogHeader>

        <DialogDescription id="inactivity-description">
          <FormattedMessage
            defaultMessage="Du har varit inaktiv ett tag. Du kommer loggas ut om {time} om du inte interagerar med sidan."
            values={{
              time: (
                <span className="font-semibold">
                  {minutes}:{seconds.toString().padStart(2, '0')}
                </span>
              ),
            }}
          />
        </DialogDescription>

        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        >
          <FormattedMessage
            defaultMessage="{minutes} minuter och {seconds} sekunder kvar"
            values={{ minutes, seconds }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
